import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  ArrowLeft, Plus, Trash, CurrencyEur, Sparkle,
  Check, Package, PencilSimple, BookmarkSimple
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import ProductSelectorModal from '../components/ProductSelectorModal';
import QuoteItem from '../components/QuoteItem';
import DrawingEditorDialog, { defaultSpec } from '../components/DrawingEditorDialog';
import NewClientDialog from '../components/NewClientDialog';
import {
  productCategories,
  profileTypes,
  glassTypes,
  openingTypes,
  glazingBeadTypes,
  thresholdOptions,
  defaultColors,
  calculateProductPrice,
  getProductType,
  defaultSize,
  doorPanels,
  catalogPricePerSqm,
  getAllProductTypes
} from '../config/productConfig';

import { API } from '../config/api';

export default function QuoteBuilderPage() {
  const [searchParams] = useSearchParams();
  const { id: quoteId } = useParams();
  const isEditMode = !!quoteId;
  const clientIdParam = searchParams.get('client');
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [clients, setClients] = useState([]);
  const [priceList, setPriceList] = useState({});
  const [customProfiles, setCustomProfiles] = useState([]);
  const [selectedClient, setSelectedClient] = useState(clientIdParam || '');
  const [items, setItems] = useState([]);
  const [extras, setExtras] = useState({});
  const [extraServices, setExtraServices] = useState([]);
  const [extraProducts, setExtraProducts] = useState([]);
  const [discount, setDiscount] = useState({ percent: 0, amount: 0, description: '' });
  const [notes, setNotes] = useState('');
  const [cantiere, setCantiere] = useState('');
  const [cantiereEdited, setCantiereEdited] = useState(false);
  const [validityDays, setValidityDays] = useState(30);
  const [taxPercent, setTaxPercent] = useState(22);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [quoteNumber, setQuoteNumber] = useState('');
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [newClientOpen, setNewClientOpen] = useState(false);
  
  // Helper: find profile ID by name (for loading saved quotes)
  const findProfileIdByName = (name, cpList) => {
    if (!name) return 'pvc';
    const base = profileTypes.find(p => p.name === name);
    if (base) return base.id;
    const custom = cpList.find(cp => cp.name === name);
    if (custom) return custom.id;
    return 'pvc';
  };
  
  // Static version of getPriceForProfile (uses passed parameters instead of state)
  const getPriceForProfileStatic = (subcategoryId, profileId, pl, cpList) => {
    const customProfile = cpList.find(cp => cp.id === profileId);
    if (customProfile) {
      const parentId = customProfile.parent_profile_id;
      const parentPrice = pl[subcategoryId]?.[parentId] || catalogPricePerSqm(subcategoryId, parentId);
      return Math.round(parentPrice * (1 + customProfile.percentage_markup / 100));
    }
    if (pl[subcategoryId] && typeof pl[subcategoryId] === 'object') {
      return pl[subcategoryId][profileId] || pl[subcategoryId]['pvc'] || catalogPricePerSqm(subcategoryId, profileId);
    }
    return pl[subcategoryId] || catalogPricePerSqm(subcategoryId, profileId);
  };

  // Product selector modal
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [extraLibrary, setExtraLibrary] = useState([]);

  // AI Quick Suggest
  const [aiQuickText, setAiQuickText] = useState('');
  const [aiQuickLoading, setAiQuickLoading] = useState(false);

  const runAiQuickSuggest = async () => {
    const text = aiQuickText.trim();
    if (!text) return;
    setAiQuickLoading(true);
    try {
      const { data } = await axios.post(`${API}/ai/quick-suggest`, { text });
      // Add as Prodotti Extra (non-blocking: user imposta prezzo manualmente)
      const dimStr = (data.width_mm && data.height_mm) ? ` ${data.width_mm}x${data.height_mm}mm` : '';
      const name = `${data.product_name}${dimStr}`;
      setExtraProducts([
        ...extraProducts,
        {
          name,
          unit_price: 0,
          quantity: data.quantity || 1,
          iva: 22,
          unit_of_measure: data.unit_of_measure || '',
        }
      ]);
      setAiQuickText('');
      const confTag = data.confidence === 'low' ? ' (verifica bene il risultato)' : '';
      toast.success(`Aggiunto: ${data.product_name}${confTag}`);
    } catch (err) {
      toast.error("Impossibile interpretare il testo. Prova con più dettagli.");
    } finally {
      setAiQuickLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientsRes, priceListRes, profilesRes, libRes] = await Promise.all([
          axios.get(`${API}/clients`),
          axios.get(`${API}/settings/price-list`).catch(() => ({ data: { price_list: {} } })),
          axios.get(`${API}/settings/custom-profiles`).catch(() => ({ data: { custom_profiles: [] } })),
          axios.get(`${API}/settings/extra-products-library`).catch(() => ({ data: { library: [] } }))
        ]);
        setClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);
        const pl = priceListRes.data.price_list || {};
        setPriceList(pl);
        const cp = profilesRes.data.custom_profiles || [];
        setCustomProfiles(cp);
        setExtraLibrary(libRes.data.library || []);

        // Load existing quote for edit mode
        if (isEditMode) {
          const { data: quote } = await axios.get(`${API}/quotes/${quoteId}`);
          setQuoteNumber(quote.quote_number || '');
          setSelectedClient(quote.client_id || '');
          setNotes(quote.notes || '');
          setCantiere(quote.cantiere || '');
          setCantiereEdited(Boolean(quote.cantiere));
          setValidityDays(quote.validity_days || 30);
          setTaxPercent(quote.tax_percent || 22);
          setDiscount({
            percent: quote.discount_percent || 0,
            amount: quote.discount_amount || 0,
            description: quote.discount_description || ''
          });

          // Rebuild extras (from new extra_services array)
          if (quote.extra_services && quote.extra_services.length > 0) {
            setExtraServices((quote.extra_services || []).map(s => ({
              name: s.name || '',
              unit_price: s.unit_price || 0,
              quantity: s.quantity || 1,
              iva: s.iva != null ? s.iva : 22
            })));
          } else {
            // Legacy: rebuild from fixed cost fields
            const legacySvcs = [];
            if (quote.installation_cost > 0) legacySvcs.push({ name: 'Installazione', unit_price: quote.installation_cost, quantity: 1, iva: 22 });
            if (quote.transport_cost > 0) legacySvcs.push({ name: 'Trasporto', unit_price: quote.transport_cost, quantity: 1, iva: 22 });
            if (quote.removal_cost > 0) legacySvcs.push({ name: 'Rimozione vecchi infissi', unit_price: quote.removal_cost, quantity: 1, iva: 22 });
            if (quote.motorized_cost > 0) legacySvcs.push({ name: 'Motorizzazione', unit_price: quote.motorized_cost, quantity: 1, iva: 22 });
            if (legacySvcs.length > 0) setExtraServices(legacySvcs);
          }

          // Load extra products
          if (quote.extra_products && quote.extra_products.length > 0) {
            setExtraProducts((quote.extra_products || []).map(p => ({
              name: p.name || '',
              unit_price: p.unit_price || 0,
              quantity: p.quantity || 1,
              iva: p.iva != null ? p.iva : 22,
              unit_of_measure: p.unit_of_measure || ''
            })));
          }

          // Rebuild items from quote data
          const loadedItems = (quote.items || []).map(item => {
            // Find product type by searching all types (since we only have product_id/subcategoryId)
            const allTypes = getAllProductTypes();
            const foundType = allTypes.find(t => t.subcategoryId === item.product_id);
            const profileId = findProfileIdByName(item.profile_name, cp);
            const unitPrice = getPriceForProfileStatic(item.product_id, profileId, pl, cp);
            const isCustom = item.product_id === 'custom' || !foundType;
            
            // Find icon: use saved icon, or look up from product config, or null for custom
            let itemIcon = item.icon || null;
            if (!itemIcon && !isCustom && foundType) {
              itemIcon = foundType.icon;
            }
            if (isCustom) itemIcon = null;
            
            return {
              product_type_id: item.product_id || '',
              product_type_name: (item.product_name || '').split(' - ')[0],
              category_id: foundType?.categoryId || '',
              category_name: foundType?.categoryName || '',
              icon: itemIcon,
              transom_type: item.transom_type || null,
              drawing: item.drawing || null,
              drawing_svg: item.drawing_svg || null,
              width: item.width || 0,
              height: item.height || 0,
              area_m2: item.width > 0 && item.height > 0 ? Math.round(item.width * item.height / 10000) / 100 : 0,
              quantity: item.quantity || 1,
              profile: profileId,
              // Option ids are saved by AI Sopralluogo pricing; builder-saved quotes fall back to defaults
              color: item.color_id || 'bianco_9010',
              glass_type: item.glass_type_id || 'doppio_4_16_4',
              opening_type: item.opening_type_id || 'battente',
              door_panel: item.door_panel || null,
              glazing_bead: 'standard',
              threshold: 'standard',
              // Off-catalog items keep their own price (0 = still to be priced), never the 250 €/m² fallback
              unit_price: isCustom ? (item.unit_price || 0) : (item.unit_price || unitPrice),
              total_price: item.total_price || 0,
              iva: item.iva != null ? item.iva : 22,
              desc_1: item.desc_1 || '',
              desc_2: item.desc_2 || '',
              desc_3: item.desc_3 || '',
              desc_4: item.desc_4 || '',
              desc_5: item.desc_5 || '',
              desc_6: item.desc_6 || '',
              unit_of_measure: item.unit_of_measure || '',
              price_override: typeof item.price_override === 'boolean' ? item.price_override : item.unit_price !== unitPrice,
            };
          });
          setItems(loadedItems);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        if (isEditMode) toast.error(t('quoteBuilder.errorLoading'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isEditMode, quoteId]);

  // Get price for a specific product type and profile (base or custom)
  const getPriceForProfile = (subcategoryId, profileId) => {
    // Check if it's a custom profile
    const customProfile = (customProfiles || []).find(cp => cp.id === profileId);
    if (customProfile) {
      const parentId = customProfile.parent_profile_id;
      const parentPrice = priceList[subcategoryId]?.[parentId] || catalogPricePerSqm(subcategoryId, parentId);
      return Math.round(parentPrice * (1 + customProfile.percentage_markup / 100));
    }
    // Base profile pricing; missing entries fall back to the catalog default for this profile
    if (priceList[subcategoryId] && typeof priceList[subcategoryId] === 'object') {
      return priceList[subcategoryId][profileId] || priceList[subcategoryId]['pvc'] || catalogPricePerSqm(subcategoryId, profileId);
    }
    return priceList[subcategoryId] || catalogPricePerSqm(subcategoryId, profileId);
  };

  // Build grouped profile list for dropdown
  const getGroupedProfiles = () => {
    const groups = [];
    profileTypes.forEach(base => {
      groups.push({ ...base, isCustom: false, description: '' });
      customProfiles
        .filter(cp => cp.parent_profile_id === base.id)
        .forEach(cp => {
          groups.push({
            id: cp.id,
            name: `${cp.name} (+${cp.percentage_markup}%)`,
            shortName: cp.name,
            isCustom: true,
            parentName: base.shortName,
            description: cp.description || ''
          });
        });
    });
    return groups;
  };

  const groupedProfiles = React.useMemo(() => getGroupedProfiles(), [customProfiles]);

  // Get profile description for an item
  const getProfileDescription = (profileId) => {
    const cp = (customProfiles || []).find(p => p.id === profileId);
    return cp?.description || '';
  };

  // Custom drawing editor for one product line
  const [drawingIndex, setDrawingIndex] = useState(null);
  const openDrawingEditor = useCallback((index) => setDrawingIndex(index), []);
  const drawingItem = drawingIndex !== null ? items[drawingIndex] : null;
  const drawingStartSpec = drawingItem ? (drawingItem.drawing || defaultSpec({
    width: drawingItem.width, height: drawingItem.height,
    sashes: parseInt(((drawingItem.icon || '').match(/(?:open_(?:pf)?|_)(\d)(?:_|ante|anta|$)/) || (drawingItem.product_type_name || '').match(/(\d)\s*ant/i) || [])[1], 10) || 1,
    kind: /portafinestr|(^|_)pf/i.test(`${drawingItem.category_id} ${drawingItem.icon} ${drawingItem.product_type_name}`) ? 'portafinestra' : 'finestra',
  })) : null;
  const setItemDrawing = (index, drawing, svg) => {
    setItems(prev => prev.map((it, i) => (i === index ? { ...it, drawing, drawing_svg: svg } : it)));
  };

  const openProductSelector = (itemIndex = null) => {
    setEditingItemIndex(itemIndex);
    setIsProductModalOpen(true);
  };

  const handleProductSelected = ({ categoryId, subcategoryId, openingTypeId, openingDetail, openingIconKey, transomType, doorPanel }) => {
    const productType = getProductType(categoryId, subcategoryId);
    if (!productType) {
      // Never drop a selection silently: a missing catalog entry is a configuration error
      console.error('Product not found in catalog:', categoryId, subcategoryId);
      toast.error('Prodotto non disponibile nel catalogo. Segnalalo all\'assistenza.');
      return;
    }

    const defaultProfile = 'pvc';
    const panel = doorPanels.find(p => p.id === doorPanel);
    const { width: startWidth, height: startHeight } = defaultSize(categoryId, subcategoryId);
    const newItem = {
      product_type_id: subcategoryId,
      product_type_name: panel
        ? `${productType.subcategory.name} (${panel.name.toLowerCase()})`
        : productType.subcategory.name,
      category_id: categoryId,
      category_name: productType.category.name,
      icon: openingIconKey || productType.subcategory.icon,
      transom_type: transomType || null,
      quantity: 1,
      width: startWidth,
      height: startHeight,
      profile: defaultProfile,
      // Interior doors are wooden leaves: no glass unless the user picks one
      glass_type: categoryId === 'porte_interne' ? 'none' : 'doppio',
      // Without an explicit choice, anta-ribalta only makes sense for windows and French doors
      opening_type: openingTypeId || (['finestre', 'portafinestre'].includes(categoryId) ? 'anta_ribalta'
        : subcategoryId.startsWith('porta_interna_scorrevole') ? 'scorrevole' : 'battente'),
      opening_detail: openingDetail || null,
      door_panel: panel ? panel.id : null,
      glazing_bead: 'tondo',
      threshold: productType.category.hasThresholdOption ? 'standard' : null,
      color: 'bianco_9010',
      hasThresholdOption: productType.category.hasThresholdOption || false,
      iva: 22,
      notes: '',
      desc_1: '', desc_2: '', desc_3: '', desc_4: '', desc_5: '', desc_6: '',
      unit_of_measure: '',
      price_override: false
    };

    // Calculate initial price with profile-specific pricing
    const profilePrice = getPriceForProfile(subcategoryId, defaultProfile);
    const priceCalc = calculateProductPrice({
      basePrice: productType.subcategory.defaultPrice,
      profilePrice,
      width: newItem.width,
      height: newItem.height,
      glassType: newItem.glass_type,
      openingType: newItem.opening_type,
      glazingBead: newItem.glazing_bead,
      threshold: newItem.threshold,
      color: newItem.color,
      doorPanel: newItem.door_panel,
      quantity: newItem.quantity
    });
    
    newItem.unit_price = priceCalc.unitPrice;
    newItem.total_price = priceCalc.totalPrice;
    newItem.area_m2 = priceCalc.areaM2;

    if (editingItemIndex !== null) {
      // When replacing a product, preserve the user's existing configuration
      const existing = items[editingItemIndex];
      if (existing) {
        newItem.width = existing.width || 1000;
        newItem.height = existing.height || 1000;
        newItem.quantity = existing.quantity || 1;
        newItem.profile = existing.profile || defaultProfile;
        newItem.color = existing.color || 'bianco_9010';
        newItem.glass_type = existing.glass_type || 'doppio';
        newItem.glazing_bead = existing.glazing_bead || 'tondo';
        newItem.iva = existing.iva ?? 22;
        newItem.notes = existing.notes || '';
        for (let d = 1; d <= 6; d++) newItem[`desc_${d}`] = existing[`desc_${d}`] || '';
        newItem.unit_of_measure = existing.unit_of_measure || '';
        // Recalculate price with preserved dimensions
        const profilePrice = getPriceForProfile(subcategoryId, newItem.profile);
        const priceCalc2 = calculateProductPrice({
          basePrice: productType.subcategory.defaultPrice,
          profilePrice,
          width: newItem.width,
          height: newItem.height,
          glassType: newItem.glass_type,
          openingType: newItem.opening_type,
          glazingBead: newItem.glazing_bead,
          threshold: newItem.threshold,
          color: newItem.color,
          doorPanel: newItem.door_panel,
          quantity: newItem.quantity
        });
        newItem.unit_price = priceCalc2.unitPrice;
        newItem.total_price = priceCalc2.totalPrice;
        newItem.area_m2 = priceCalc2.areaM2;
      }
      const newItems = [...items];
      newItems[editingItemIndex] = newItem;
      setItems(newItems);
    } else {
      setItems([...items, newItem]);
      // Bring the new product (added at the bottom) into view
      const newIndex = items.length;
      setTimeout(() => document.querySelector(`[data-testid="quote-item-${newIndex}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
    }
    
    setIsProductModalOpen(false);
  };

  const updateItem = useCallback((index, field, value) => {
    setItems(prev => {
      const newItems = [...prev];
      newItems[index] = { ...newItems[index], [field]: value };

      // Reset price override
      if (field === '_resetPrice') {
        newItems[index].price_override = false;
        const item = newItems[index];
        const profilePrice = getPriceForProfile(item.product_type_id, item.profile);
        const productType = getProductType(item.category_id, item.product_type_id);
        const priceCalc = calculateProductPrice({
          basePrice: productType?.subcategory?.defaultPrice || 250,
          profilePrice,
          width: parseFloat(item.width) || 1000,
          height: parseFloat(item.height) || 1000,
          glassType: item.glass_type, openingType: item.opening_type,
          glazingBead: item.glazing_bead, threshold: item.threshold,
          color: item.color, doorPanel: item.door_panel, quantity: parseInt(item.quantity) || 1
        });
        newItems[index].unit_price = priceCalc.unitPrice;
        newItems[index].total_price = priceCalc.totalPrice;
        newItems[index].area_m2 = priceCalc.areaM2;
        return newItems;
      }

      // If user manually edits unit_price, activate override
      if (field === 'unit_price_manual') {
        const manualPrice = parseFloat(value) || 0;
        newItems[index].price_override = true;
        newItems[index].unit_price = manualPrice;
        newItems[index].total_price = manualPrice * (parseInt(newItems[index].quantity) || 1);
        newItems[index].area_m2 = ((parseFloat(newItems[index].width) || 1000) * (parseFloat(newItems[index].height) || 1000)) / 1000000;
        return newItems;
      }

      // Skip desc fields from triggering price recalc
      if (field.startsWith('desc_')) {
        return newItems;
      }

      // If price_override is active, only recalc total (unit stays manual)
      if (newItems[index].price_override) {
        const qty = parseInt(newItems[index].quantity) || 1;
        newItems[index].total_price = newItems[index].unit_price * qty;
        newItems[index].area_m2 = ((parseFloat(newItems[index].width) || 1000) * (parseFloat(newItems[index].height) || 1000)) / 1000000;
        return newItems;
      }

      // Recalculate price with profile-specific pricing
      const item = newItems[index];
      const profilePrice = getPriceForProfile(item.product_type_id, item.profile);
      const productType = getProductType(item.category_id, item.product_type_id);
      const priceCalc = calculateProductPrice({
        basePrice: productType?.subcategory?.defaultPrice || 250,
        profilePrice,
        width: parseFloat(item.width) || 1000,
        height: parseFloat(item.height) || 1000,
        glassType: item.glass_type, openingType: item.opening_type,
        glazingBead: item.glazing_bead, threshold: item.threshold,
        color: item.color, doorPanel: item.door_panel, quantity: parseInt(item.quantity) || 1
      });

      newItems[index].unit_price = priceCalc.unitPrice;
      newItems[index].total_price = priceCalc.totalPrice;
      newItems[index].area_m2 = priceCalc.areaM2;

      return newItems;
    });
  }, [getPriceForProfile]);

  const onIconChange = useCallback((index, newIcon) => {
    setItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], icon: newIcon };
      return updated;
    });
  }, []);

  const onNameChange = useCallback((index, value) => {
    setItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], product_type_name: value };
      return updated;
    });
  }, []);

  const removeItem = useCallback((index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  }, []);

  const duplicateItem = useCallback((index) => {
    setItems(prev => {
      const original = prev[index];
      if (!original) return prev;
      const clone = { ...original, product_id: `${original.product_id}_copy_${Date.now()}` };
      const newItems = [...prev];
      newItems.splice(index + 1, 0, clone);
      return newItems;
    });
    toast.success('Prodotto duplicato');
  }, []);

  // Last text copied from a description: lets "Incolla" work even where the browser
  // does not allow reading the system clipboard (Firefox, pages without permission)
  const lastCopiedRef = useRef('');

  const copyDescField = useCallback((value) => {
    if (value) {
      lastCopiedRef.current = value;
      navigator.clipboard?.writeText(value).catch(() => {});
      toast.success(t('quoteBuilder.textCopied'));
    }
  }, [t]);

  const pasteDescField = useCallback(async (index, field) => {
    let text = '';
    try {
      text = navigator.clipboard?.readText ? await navigator.clipboard.readText() : '';
    } catch (e) { /* permission refused: fall back to the last copied description */ }
    text = (text || lastCopiedRef.current || '').trim();
    if (!text) {
      toast.error('Niente da incollare: copia prima un testo, oppure incolla nel campo con Ctrl+V');
      return;
    }
    updateItem(index, field, text);
    toast.success('Testo incollato');
  }, [updateItem]);

  const saveExtraToLibrary = async (idx) => {
    const prod = extraProducts[idx];
    if (!prod || !prod.name || !prod.name.trim()) {
      toast.error('Inserisci il nome del prodotto prima di salvarlo');
      return;
    }
    const price = parseFloat(prod.unit_price) || 0;
    const iva = parseFloat(prod.iva) || 22;
    const name = prod.name.trim();
    const unit = prod.unit_of_measure || '';
    // Check if already in library (case-insensitive)
    const exists = (extraLibrary || []).some(l => (l.name || '').trim().toLowerCase() === name.toLowerCase());
    if (exists) {
      toast.info(`"${name}" è già in libreria`);
      return;
    }
    try {
      const newLib = [...(extraLibrary || []), { name, unit_price: price, iva, description: '', unit_of_measure: unit }];
      const { data } = await axios.put(`${API}/settings/extra-products-library`, { library: newLib });
      setExtraLibrary(data.library || newLib);
      toast.success(`"${name}" salvato in libreria`);
    } catch (err) {
      toast.error('Errore nel salvataggio in libreria');
    }
  };

  const fetchAiSuggestions = async () => {
    if ((items || []).length === 0) {
      toast.error(t('quoteBuilder.addAtLeastOneProduct'));
      return;
    }
    setLoadingSuggestions(true);
    try {
      const allProfiles = [...profileTypes, ...(customProfiles || []).map(cp => ({ id: cp.id, name: cp.name }))];
      const apiItems = (items || []).map(item => ({
        product_name: item.product_type_name,
        quantity: item.quantity,
        unit_price: item.unit_price,
        profile_name: allProfiles.find(p => p.id === item.profile)?.name || 'PVC'
      }));
      const clientName = (clients || []).find(c => c.id === selectedClient)?.name || '';
      const { data } = await axios.post(`${API}/ai/suggestions`, {
        items: apiItems,
        client_name: clientName,
        total: calculateTotal()
      });
      setAiSuggestions(data || {})
    } catch (err) {
      toast.error(t('quoteBuilder.aiError'));
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const calculateSubtotal = () => {
    const itemsTotal = (items || []).reduce((sum, item) => sum + (item.total_price || 0), 0);
    const extraProdTotal = (extraProducts || []).reduce((sum, p) => sum + (parseFloat(p.unit_price) || 0) * (parseInt(p.quantity) || 0), 0);
    return itemsTotal + extraProdTotal;
  };

  const calculateExtrasTotal = () => {
    return (extraServices || []).reduce((total, svc) => {
      return total + (parseFloat(svc.unit_price) || 0) * (parseInt(svc.quantity) || 0);
    }, 0);
  };

  const calculateDiscount = () => {
    const subtotal = calculateSubtotal() + calculateExtrasTotal();
    if (discount.amount > 0) return parseFloat(discount.amount);
    return subtotal * (parseFloat(discount.percent) / 100);
  };

  const calculateItemsIVA = () => {
    return (items || []).reduce((sum, item) => {
      const rate = parseFloat(item.iva) || 22;
      return sum + ((item.total_price || 0) * rate / 100);
    }, 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal() + calculateExtrasTotal();
    const discountVal = calculateDiscount();
    const afterDiscount = subtotal - discountVal;
    // Per-item IVA on products
    const productSubtotal = calculateSubtotal();
    const discountRatio = productSubtotal > 0 ? Math.max(0, productSubtotal - discountVal) / productSubtotal : 1;
    let totalIVA = 0;
    (items || []).forEach(item => {
      const rate = parseFloat(item.iva) || 22;
      totalIVA += (item.total_price || 0) * discountRatio * rate / 100;
    });
    // Per-product IVA on extra products
    (extraProducts || []).forEach(p => {
      const pTotal = (parseFloat(p.unit_price) || 0) * (parseInt(p.quantity) || 0);
      const pIva = parseFloat(p.iva) || 22;
      totalIVA += pTotal * discountRatio * pIva / 100;
    });
    // Per-service IVA on extras
    (extraServices || []).forEach(svc => {
      const svcTotal = (parseFloat(svc.unit_price) || 0) * (parseInt(svc.quantity) || 0);
      const svcIva = parseFloat(svc.iva) || 22;
      totalIVA += svcTotal * svcIva / 100;
    });
    return afterDiscount + totalIVA;
  };

  const handleSubmit = async () => {
    if (!selectedClient) {
      toast.error(t('quoteBuilder.selectClientToast'));
      return;
    }
    if ((items || []).length === 0) {
      toast.error(t('quoteBuilder.addAtLeastOneProduct'));
      return;
    }

    setSaving(true);
    try {
      // Transform items for API
      const apiItems = (items || []).map(item => {
        const profileName = [...profileTypes, ...(customProfiles || []).map(cp => ({ id: cp.id, name: cp.name }))].find(p => p.id === item.profile)?.name || '';
        const profileDesc = getProfileDescription(item.profile);
        const colorName = (item.color && item.color !== 'none') ? (defaultColors.find(c => c.id === item.color)?.name || '') : '';
        const glassName = (item.glass_type && item.glass_type !== 'none') ? (glassTypes.find(g => g.id === item.glass_type)?.name || '') : '';
        
        let noteParts = [profileName, colorName, glassName].filter(Boolean).join(', ');
        if (profileDesc) noteParts += `\n${t('quoteBuilder.profileLabel')} ${profileDesc}`;

        return {
          product_id: item.product_type_id,
          product_name: (item.width > 0 && item.height > 0) 
            ? `${item.product_type_name} - ${item.width}x${item.height}mm`
            : item.product_type_name,
          quantity: parseInt(item.quantity) || 1,
          width: parseFloat(item.width) || 0,
          height: parseFloat(item.height) || 0,
          unit_price: item.unit_price,
          total_price: item.total_price,
          iva: parseFloat(item.iva) || 22,
          icon: item.icon || null,
          profile_name: profileName,
          profile_description: profileDesc,
          notes: noteParts,
          desc_1: item.desc_1 || null,
          desc_2: item.desc_2 || null,
          desc_3: item.desc_3 || null,
          desc_4: item.desc_4 || null,
          desc_5: item.desc_5 || null,
          desc_6: item.desc_6 || null,
          transom_type: item.transom_type || null,
          drawing: item.drawing || null,
          unit_of_measure: item.unit_of_measure || null,
          color_id: item.color || null,
          glass_type_id: item.glass_type || null,
          opening_type_id: item.opening_type || null,
          price_override: Boolean(item.price_override),
          door_panel: item.door_panel || null
        };
      });

      const quoteData = {
        client_id: selectedClient,
        items: apiItems,
        extra_services: (extraServices || []).filter(s => s.name.trim() && (parseFloat(s.unit_price) || 0) > 0).map(s => ({
          name: s.name.trim(),
          unit_price: parseFloat(s.unit_price) || 0,
          quantity: parseInt(s.quantity) || 1,
          iva: parseFloat(s.iva) || 22
        })),
        extra_products: (extraProducts || []).filter(p => p.name.trim() && (parseFloat(p.unit_price) || 0) > 0).map(p => ({
          name: p.name.trim(),
          unit_price: parseFloat(p.unit_price) || 0,
          quantity: parseInt(p.quantity) || 1,
          iva: parseFloat(p.iva) || 22,
          unit_of_measure: p.unit_of_measure || null
        })),
        installation_cost: 0,
        transport_cost: 0,
        removal_cost: 0,
        motorized_cost: 0,
        special_glass_cost: 0,
        discount_percent: parseFloat(discount.percent) || 0,
        discount_amount: parseFloat(discount.amount) || 0,
        discount_description: discount.description || '',
        notes,
        cantiere: (cantiere || '').trim() || null,
        validity_days: parseInt(validityDays) || 30,
        tax_percent: parseFloat(taxPercent) || 22
      };

      if (isEditMode) {
        await axios.put(`${API}/quotes/${quoteId}`, quoteData);
        toast.success(t('quoteBuilder.quoteUpdated'));
        navigate(`/quotes/${quoteId}`);
      } else {
        const { data } = await axios.post(`${API}/quotes`, quoteData);
        toast.success(t('quoteBuilder.quoteCreated'));
        navigate(`/quotes/${data.id}`);
      }
    } catch (err) {
      console.error('Error saving quote:', err);
      toast.error(isEditMode ? t('quoteBuilder.errorUpdate') : t('quoteBuilder.errorCreate'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0033A0]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="quote-builder-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/quotes')}
          className="text-[#4A5568]"
          data-testid="back-button"
        >
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#0A0F1A]">
            {isEditMode ? t('quoteBuilder.editTitleWith', { number: quoteNumber }) : t('quoteBuilder.title')}
          </h1>
          <p className="text-[#4A5568] mt-1">
            {isEditMode ? t('quoteBuilder.editSubtitle') : t('quoteBuilder.createSubtitle')}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client Selection */}
          <Card className="border border-[#E2E8F0] rounded-sm">
            <CardHeader className="border-b border-[#E2E8F0] pb-4">
              <CardTitle className="font-outfit text-lg font-semibold text-[#0A0F1A]">
                {t('quoteBuilder.selectClientTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <Select value={selectedClient} onValueChange={(id) => {
                    setSelectedClient(id);
                    // Auto-populate cantiere with client preferred cantiere or address if user hasn't edited it
                    if (!cantiereEdited) {
                      const c = (clients || []).find(x => x.id === id);
                      if (c) setCantiere(c.cantiere || c.address || '');
                    }
                  }}>
                    <SelectTrigger className="border-[#E2E8F0]" data-testid="client-select">
                      <SelectValue placeholder={t('quoteBuilder.selectClientPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {(clients || []).map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name} {client.address ? `- ${client.address}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0 border-[#0033A0] text-[#0033A0] hover:bg-[#F0F4FF]"
                  onClick={() => setNewClientOpen(true)}
                  data-testid="quote-new-client-btn"
                  title="Crea un nuovo cliente"
                >
                  <Plus size={16} className="sm:mr-1" />
                  <span className="hidden sm:inline">Nuovo</span>
                </Button>
              </div>
              {selectedClient && (
                <div className="mt-3 pt-3 border-t border-[#E2E8F0]">
                  <Label htmlFor="cantiere" className="flex items-center gap-2 text-xs text-[#4A5568]">
                    Indirizzo cantiere
                    <span className="text-[10px] text-[#A0AEC0] font-normal">(luogo di installazione)</span>
                  </Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      id="cantiere"
                      value={cantiere}
                      onChange={(e) => { setCantiere(e.target.value); setCantiereEdited(true); }}
                      placeholder="Indirizzo del cantiere (default: indirizzo cliente)"
                      className="border-[#E2E8F0]"
                      data-testid="cantiere-input"
                    />
                    {(() => {
                      const c = (clients || []).find(x => x.id === selectedClient);
                      const addr = c?.address || '';
                      if (addr && cantiere !== addr) {
                        return (
                          <Button
                            type="button" variant="outline" size="sm"
                            onClick={() => { setCantiere(addr); setCantiereEdited(true); }}
                            className="text-xs shrink-0"
                            data-testid="cantiere-use-client-address"
                          >
                            Usa indirizzo cliente
                          </Button>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Products */}
          <Card className="border border-[#E2E8F0] rounded-sm">
            <CardHeader className="border-b border-[#E2E8F0] pb-4 flex flex-row items-center justify-between">
              <CardTitle className="font-outfit text-lg font-semibold text-[#0A0F1A]">
                {t('quoteBuilder.addProductsTitle')}
              </CardTitle>
              <Button
                size="sm"
                onClick={() => openProductSelector()}
                className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
                data-testid="add-product-button"
              >
                <Plus size={18} className="mr-1" />
                {t('quoteBuilder.addBtn')}
              </Button>
            </CardHeader>
            {/* AI Quick Suggest row */}
            <div className="px-4 pt-3 pb-1 border-b border-[#E2E8F0] bg-[#F8FAFF]">
              <div className="flex items-center gap-2">
                <Sparkle size={16} className="text-[#0033A0] shrink-0" weight="fill" />
                <Input
                  placeholder="Aggiungi rapido con IA: es. 'PF2 120x220 pvc noce' poi premi Invio..."
                  value={aiQuickText}
                  onChange={(e) => setAiQuickText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runAiQuickSuggest(); } }}
                  disabled={aiQuickLoading}
                  className="h-8 text-sm border-[#E2E8F0] bg-white"
                  data-testid="ai-quick-input"
                />
                <Button
                  size="sm"
                  onClick={runAiQuickSuggest}
                  disabled={aiQuickLoading || !aiQuickText.trim()}
                  className="bg-[#0033A0] hover:bg-[#0047AB] text-white h-8 px-3 shrink-0"
                  data-testid="ai-quick-button"
                >
                  {aiQuickLoading ? <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" /> : <Sparkle size={14} weight="fill" />}
                  <span className="ml-1 text-xs">Suggerisci</span>
                </Button>
              </div>
              <p className="text-[10px] text-[#4A5568] mt-1.5 ml-6">
                Il suggerimento userà le tue sigle personalizzate e le correzioni imparate
              </p>
            </div>
            <CardContent className="p-4">
              {(items || []).length === 0 ? (
                <div className="text-center py-8">
                  <Package size={48} className="mx-auto text-[#4A5568] mb-3" />
                  <p className="text-[#4A5568] mb-4">
                    {t('quoteBuilder.clickAddToStart')}
                  </p>
                  <Button
                    onClick={() => openProductSelector()}
                    className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
                  >
                    <Plus size={18} className="mr-2" />
                    {t('quoteBuilder.addProduct')}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {(items || []).map((item, index) => (
                    <QuoteItem
                      key={index}
                      item={item}
                      index={index}
                      updateItem={updateItem}
                      removeItem={removeItem}
                      duplicateItem={duplicateItem}
                      onIconChange={onIconChange}
                      onNameChange={onNameChange}
                      onChangeProduct={openProductSelector}
                      onEditDrawing={openDrawingEditor}
                      groupedProfiles={groupedProfiles}
                      defaultColors={defaultColors}
                      glassTypes={glassTypes}
                      openingTypes={openingTypes}
                      glazingBeadTypes={glazingBeadTypes}
                      thresholdOptions={thresholdOptions}
                      getProfileDescription={getProfileDescription}
                      copyDescField={copyDescField}
                      pasteDescField={pasteDescField}
                    />
                  ))}
                  {/* Add the next product right below the last one, without scrolling back up */}
                  <Button
                    variant="outline"
                    onClick={() => openProductSelector()}
                    className="w-full border-dashed border-2 border-[#0033A0]/40 text-[#0033A0] hover:bg-[#0033A0]/5 hover:border-[#0033A0] h-11"
                    data-testid="add-product-bottom-button"
                  >
                    <Plus size={18} className="mr-2" />
                    {t('quoteBuilder.addProduct')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Extra Products - Dynamic List (printed in products table in PDF) */}
          <Card className="border border-[#E2E8F0] rounded-sm">
            <CardHeader className="border-b border-[#E2E8F0] pb-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="font-outfit text-lg font-semibold text-[#0A0F1A]">
                  Prodotti Extra
                </CardTitle>
                <div className="flex gap-2 items-center">
                  {(extraLibrary || []).length > 0 && (
                    <Select
                      value=""
                      onValueChange={(libId) => {
                        const lib = (extraLibrary || []).find(l => l.id === libId);
                        if (lib) {
                          setExtraProducts([...extraProducts, {
                            name: lib.name,
                            unit_price: parseFloat(lib.unit_price) || 0,
                            quantity: 1,
                            iva: parseFloat(lib.iva) || 22,
                            unit_of_measure: lib.unit_of_measure || ''
                          }]);
                        }
                      }}
                    >
                      <SelectTrigger className="h-8 text-xs w-[180px]" data-testid="extra-product-from-library">
                        <SelectValue placeholder="Dalla libreria..." />
                      </SelectTrigger>
                      <SelectContent>
                        {(extraLibrary || []).map(lib => (
                          <SelectItem key={lib.id} value={lib.id}>
                            {lib.name} — €{parseFloat(lib.unit_price || 0).toFixed(2)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <Button
                    variant="outline" size="sm"
                    onClick={() => setExtraProducts([...extraProducts, { name: '', unit_price: 0, quantity: 1, iva: 22, unit_of_measure: '' }])}
                    data-testid="add-extra-product"
                  >
                    <Plus size={16} className="mr-1" /> Aggiungi
                  </Button>
                </div>
              </div>
              <p className="text-xs text-[#4A5568] mt-1">Prodotti aggiuntivi liberi (appaiono nella tabella prodotti del PDF)</p>
            </CardHeader>
            <CardContent className="p-4">
              {(extraProducts || []).length === 0 ? (
                <p className="text-sm text-[#A0AEC0] text-center py-4">Nessun prodotto extra</p>
              ) : (
                <div className="space-y-3">
                  {(extraProducts || []).map((prod, idx) => (
                    <div key={idx} className="flex flex-col gap-2 p-3 bg-[#F4F6F8] rounded-lg relative" data-testid={`extra-product-row-${idx}`}>
                      <div className="flex items-center gap-2">
                        <Input
                          placeholder="Nome prodotto"
                          value={prod.name}
                          onChange={(e) => {
                            const arr = [...extraProducts];
                            arr[idx] = { ...arr[idx], name: e.target.value };
                            setExtraProducts(arr);
                          }}
                          className="flex-1 h-8 text-sm"
                          data-testid={`extra-product-name-${idx}`}
                        />
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => saveExtraToLibrary(idx)}
                          disabled={!prod.name || !prod.name.trim()}
                          title="Salva in libreria per riutilizzarlo in altri preventivi"
                          className="text-[#0033A0] hover:bg-blue-50 h-8 w-8 p-0 disabled:opacity-40"
                          data-testid={`extra-product-save-to-library-${idx}`}
                        >
                          <BookmarkSimple size={14} />
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => setExtraProducts(extraProducts.filter((_, i) => i !== idx))}
                          className="text-red-500 hover:bg-red-50 h-8 w-8 p-0"
                          data-testid={`extra-product-remove-${idx}`}
                        >
                          <Trash size={14} />
                        </Button>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="flex-1 min-w-[100px]">
                          <div className="relative">
                            <Input
                              type="number" step="0.01" min="0" placeholder="Prezzo"
                              value={prod.unit_price || ''}
                              onChange={(e) => {
                                const arr = [...extraProducts];
                                arr[idx] = { ...arr[idx], unit_price: e.target.value };
                                setExtraProducts(arr);
                              }}
                              className="h-8 text-sm pr-5"
                              data-testid={`extra-product-price-${idx}`}
                            />
                            <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-[#4A5568]">{'\u20AC'}</span>
                          </div>
                        </div>
                        <div className="w-14">
                          <Input
                            type="number" min="1" placeholder="Qt"
                            value={prod.quantity || ''}
                            onChange={(e) => {
                              const arr = [...extraProducts];
                              arr[idx] = { ...arr[idx], quantity: parseInt(e.target.value) || 1 };
                              setExtraProducts(arr);
                            }}
                            className="h-8 text-sm text-center"
                            data-testid={`extra-product-qty-${idx}`}
                          />
                        </div>
                        <div className="w-[90px]">
                          <Select
                            value={prod.unit_of_measure || '_none'}
                            onValueChange={(v) => {
                              const arr = [...extraProducts];
                              arr[idx] = { ...arr[idx], unit_of_measure: v === '_none' ? '' : v };
                              setExtraProducts(arr);
                            }}
                          >
                            <SelectTrigger className="h-8 text-xs" data-testid={`extra-product-unit-${idx}`}>
                              <SelectValue placeholder="Unità" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_none"><span className="text-[#A0AEC0] italic">Nessuna</span></SelectItem>
                              <SelectItem value="Pezzi">Pezzi</SelectItem>
                              <SelectItem value="Kg">Kg</SelectItem>
                              <SelectItem value="MQ">MQ</SelectItem>
                              <SelectItem value="ML">ML</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-[68px]">
                          <Select
                            value={String(prod.iva ?? 22)}
                            onValueChange={(v) => {
                              const arr = [...extraProducts];
                              arr[idx] = { ...arr[idx], iva: parseFloat(v) };
                              setExtraProducts(arr);
                            }}
                          >
                            <SelectTrigger className="h-8 text-xs" data-testid={`extra-product-iva-${idx}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="4">4%</SelectItem>
                              <SelectItem value="10">10%</SelectItem>
                              <SelectItem value="22">22%</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-20 text-right text-sm font-medium text-[#0A0F1A]">
                          {'\u20AC'}{((parseFloat(prod.unit_price) || 0) * (parseInt(prod.quantity) || 0)).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Extra Services - Dynamic List */}
          <Card className="border border-[#E2E8F0] rounded-sm">
            <CardHeader className="border-b border-[#E2E8F0] pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="font-outfit text-lg font-semibold text-[#0A0F1A]">
                  {t('quoteBuilder.extraServicesTitle')}
                </CardTitle>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setExtraServices([...extraServices, { name: '', unit_price: 0, quantity: 1, iva: 22 }])}
                  className="border-[#0033A0] text-[#0033A0] h-8"
                  data-testid="add-extra-service-btn"
                >
                  <Plus size={16} className="mr-1" />
                  Aggiungi servizio
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              {(extraServices || []).length === 0 ? (
                <p className="text-sm text-[#4A5568] text-center py-4">
                  Nessun servizio aggiuntivo. Clicca "Aggiungi servizio" per iniziare.
                </p>
              ) : (
                <div className="space-y-3">
                  {(extraServices || []).map((svc, idx) => (
                    <div key={idx} className="border border-[#E2E8F0] rounded-sm p-3" data-testid={`extra-service-${idx}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <Input
                            type="text"
                            placeholder="Nome servizio (es. Installazione, Trasporto...)"
                            value={svc.name}
                            onChange={(e) => {
                              const arr = [...extraServices];
                              arr[idx] = { ...arr[idx], name: e.target.value };
                              setExtraServices(arr);
                            }}
                            className="h-8 text-sm"
                            data-testid={`extra-service-name-${idx}`}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setExtraServices((extraServices || []).filter((_, i) => i !== idx))}
                          className="text-red-500 hover:text-red-700 h-8 w-8 p-0 shrink-0"
                          data-testid={`remove-extra-service-${idx}`}
                        >
                          <Trash size={16} />
                        </Button>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="flex-1 min-w-[100px]">
                          <div className="relative">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="Prezzo"
                              value={svc.unit_price || ''}
                              onChange={(e) => {
                                const arr = [...extraServices];
                                arr[idx] = { ...arr[idx], unit_price: e.target.value };
                                setExtraServices(arr);
                              }}
                              className="h-8 text-sm pr-5"
                              data-testid={`extra-service-price-${idx}`}
                            />
                            <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-[#4A5568]">{'\u20AC'}</span>
                          </div>
                        </div>
                        <div className="w-14">
                          <Input
                            type="number"
                            min="1"
                            placeholder="Qt"
                            value={svc.quantity || ''}
                            onChange={(e) => {
                              const arr = [...extraServices];
                              arr[idx] = { ...arr[idx], quantity: parseInt(e.target.value) || 1 };
                              setExtraServices(arr);
                            }}
                            className="h-8 text-sm text-center"
                            data-testid={`extra-service-qty-${idx}`}
                          />
                        </div>
                        <div className="w-[68px]">
                          <Select
                            value={String(svc.iva ?? 22)}
                            onValueChange={(v) => {
                              const arr = [...extraServices];
                              arr[idx] = { ...arr[idx], iva: parseFloat(v) };
                              setExtraServices(arr);
                            }}
                          >
                            <SelectTrigger className="h-8 text-xs" data-testid={`extra-service-iva-${idx}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="4">4%</SelectItem>
                              <SelectItem value="10">10%</SelectItem>
                              <SelectItem value="22">22%</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-20 text-right text-sm font-medium text-[#0A0F1A]">
                          {'\u20AC'}{((parseFloat(svc.unit_price) || 0) * (parseInt(svc.quantity) || 0)).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="border border-[#E2E8F0] rounded-sm">
            <CardHeader className="border-b border-[#E2E8F0] pb-4">
              <CardTitle className="font-outfit text-lg font-semibold text-[#0A0F1A]">
                {t('quoteBuilder.notesTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('quoteBuilder.notesPlaceholder')}
                rows={3}
                data-testid="quote-notes-input"
              />
            </CardContent>
          </Card>
        </div>

        {/* Summary Sidebar */}
        <div className="space-y-6">
          <Card className="border border-[#E2E8F0] rounded-sm sticky top-4">
            <CardHeader className="border-b border-[#E2E8F0] pb-4">
              <CardTitle className="font-outfit text-lg font-semibold text-[#0A0F1A]">
                {t('quoteBuilder.summaryTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#4A5568]">{t('quoteBuilder.subtotalProducts')}</span>
                  <span className="font-medium">€{calculateSubtotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#4A5568]">{t('quoteBuilder.additionalServices')}</span>
                  <span className="font-medium">€{calculateExtrasTotal().toFixed(2)}</span>
                </div>
                
                {/* Discount */}
                <div className="pt-2 border-t border-[#E2E8F0]">
                  <Label className="text-xs">{t('quoteBuilder.discountLabel')}</Label>
                  <div className="flex gap-2 mt-1">
                    <div className="relative flex-1">
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="%"
                        value={discount.percent}
                        onChange={(e) => setDiscount({ ...discount, percent: e.target.value, amount: 0 })}
                        className="pr-6 h-8"
                        data-testid="discount-percent-input"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-[#4A5568]">%</span>
                    </div>
                    <div className="relative flex-1">
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="€"
                        value={discount.amount}
                        onChange={(e) => setDiscount({ ...discount, amount: e.target.value, percent: 0 })}
                        className="pr-6 h-8"
                        data-testid="discount-amount-input"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-[#4A5568]">€</span>
                    </div>
                  </div>
                  {(parseFloat(discount.percent) > 0 || parseFloat(discount.amount) > 0) && (
                    <Input
                      type="text"
                      placeholder={t('quoteBuilder.discountDescriptionPlaceholder')}
                      value={discount.description}
                      onChange={(e) => setDiscount({ ...discount, description: e.target.value })}
                      className="mt-1.5 h-8 text-xs"
                      data-testid="discount-description-input"
                    />
                  )}
                </div>

                {calculateDiscount() > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span>{t('quoteBuilder.discountLabel')}</span>
                    <span>-€{calculateDiscount().toFixed(2)}</span>
                  </div>
                )}

                {/* Tax - per-item IVA summary */}
                <div className="pt-2 border-t border-[#E2E8F0]">
                  <Label className="text-xs font-medium">{t('quoteBuilder.ivaPerProduct')}</Label>
                  {(() => {
                    const groups = {};
                    (items || []).forEach(item => {
                      const rate = parseFloat(item.iva) || 22;
                      groups[rate] = (groups[rate] || 0) + (item.total_price || 0);
                    });
                    // Add extra services per-rate
                    (extraServices || []).forEach(svc => {
                      const rate = parseFloat(svc.iva) || 22;
                      const svcTotal = (parseFloat(svc.unit_price) || 0) * (parseInt(svc.quantity) || 0);
                      if (svcTotal > 0) groups[rate] = (groups[rate] || 0) + svcTotal;
                    });
                    const rates = Object.keys(groups).sort((a, b) => a - b);
                    if (rates.length === 0) return (
                      <p className="text-xs text-[#4A5568] mt-1">{t('quoteBuilder.noProducts')}</p>
                    );
                    return rates.map(rate => (
                      <div key={rate} className="flex justify-between text-xs text-[#4A5568] mt-1">
                        <span>IVA {parseFloat(rate).toFixed(0)}%</span>
                        <span>€{(groups[rate] * parseFloat(rate) / 100).toFixed(2)}</span>
                      </div>
                    ));
                  })()}
                  </div>

                {/* Validity */}
                <div className="pt-2 border-t border-[#E2E8F0]">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">{t('quoteBuilder.validityLabel')}</Label>
                    <Input
                      type="number"
                      value={validityDays}
                      onChange={(e) => setValidityDays(e.target.value)}
                      className="w-16 h-8 text-sm"
                      data-testid="validity-days-input"
                    />
                    <span className="text-xs text-[#4A5568]">{t('quoteBuilder.daysLabel')}</span>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t-2 border-[#0033A0]">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-[#0A0F1A]">{t('quoteBuilder.totalLabel')}</span>
                  <span className="font-outfit text-2xl font-bold text-[#0033A0]">
                    €{calculateTotal().toFixed(2)}
                  </span>
                </div>
              </div>

              <Button
                className="w-full bg-[#0033A0] hover:bg-[#0047AB] text-white"
                onClick={handleSubmit}
                disabled={saving}
                data-testid="create-quote-button"
              >
                {saving ? (isEditMode ? t('quoteBuilder.savingInProgress') : t('quoteBuilder.creatingInProgress')) : (
                  <>
                    <Check size={20} className="mr-2" />
                    {isEditMode ? t('quoteBuilder.saveChanges') : t('quoteBuilder.createQuote')}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* AI Suggestions */}
          <Card className="border border-[#E2E8F0] rounded-sm">
            <CardHeader className="border-b border-[#E2E8F0] pb-3">
              <CardTitle className="font-outfit text-base font-semibold text-[#0A0F1A] flex items-center gap-2">
                <Sparkle size={18} className="text-[#0033A0]" weight="fill" />
                {t('quoteBuilder.aiAssistant')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <Button
                variant="outline"
                className="w-full border-[#0033A0] text-[#0033A0] hover:bg-[#F4F6F8]"
                onClick={fetchAiSuggestions}
                disabled={loadingSuggestions || (items || []).length === 0}
                data-testid="ai-suggestions-button"
              >
                {loadingSuggestions ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#0033A0]" />
                    {t('quoteBuilder.analysisInProgress')}
                  </span>
                ) : (
                  <>
                    <Sparkle size={16} className="mr-2" weight="fill" />
                    {t('quoteBuilder.aiSuggestions')}
                  </>
                )}
              </Button>
              
              {aiSuggestions && (
                <div className="mt-4 space-y-4">
                  {aiSuggestions.upsell?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-[#0033A0] uppercase tracking-wider mb-2">{t('quoteBuilder.upsell')}</h4>
                      {(aiSuggestions.upsell || []).map((tip, i) => (
                        <div key={i} className="p-2 mb-2 bg-blue-50 rounded-sm border border-blue-100">
                          <p className="text-xs font-medium text-[#0A0F1A]">{tip.title}</p>
                          <p className="text-[11px] text-[#4A5568] mt-0.5">{tip.description}</p>
                          {tip.potential_value && (
                            <p className="text-[10px] text-[#0033A0] font-medium mt-1">{tip.potential_value}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {aiSuggestions.margin_tips?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-green-700 uppercase tracking-wider mb-2">{t('quoteBuilder.margins')}</h4>
                      {(aiSuggestions.margin_tips || []).map((tip, i) => (
                        <div key={i} className="p-2 mb-2 bg-green-50 rounded-sm border border-green-100">
                          <p className="text-xs font-medium text-[#0A0F1A]">{tip.title}</p>
                          <p className="text-[11px] text-[#4A5568] mt-0.5">{tip.description}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {aiSuggestions.closing_tips?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-2">{t('quoteBuilder.closing')}</h4>
                      {(aiSuggestions.closing_tips || []).map((tip, i) => (
                        <div key={i} className="p-2 mb-2 bg-amber-50 rounded-sm border border-amber-100">
                          <p className="text-xs font-medium text-[#0A0F1A]">{tip.title}</p>
                          <p className="text-[11px] text-[#4A5568] mt-0.5">{tip.description}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Product Selector Modal */}
      <DrawingEditorDialog
        open={drawingIndex !== null}
        onOpenChange={(o) => { if (!o) setDrawingIndex(null); }}
        initialSpec={drawingStartSpec}
        onApply={(spec, svg, description, useAsName) => {
          const it = items[drawingIndex];
          setItemDrawing(drawingIndex, spec, svg);
          if (useAsName && description) onNameChange(drawingIndex, description);
          // Measures typed (or read from the sentence) in the editor become the product's, price included
          if (spec.width_mm && Number(spec.width_mm) !== Number(it?.width)) updateItem(drawingIndex, 'width', spec.width_mm);
          if (spec.height_mm && Number(spec.height_mm) !== Number(it?.height)) updateItem(drawingIndex, 'height', spec.height_mm);
        }}
        onRemove={drawingItem?.drawing ? () => setItemDrawing(drawingIndex, null, null) : null}
      />
      <ProductSelectorModal
        isOpen={isProductModalOpen}
        onClose={() => setIsProductModalOpen(false)}
        onSelectProduct={handleProductSelected}
        priceList={priceList}
      />

      {/* New Client Dialog (inline create from quote builder) */}
      <NewClientDialog
        open={newClientOpen}
        onOpenChange={setNewClientOpen}
        onCreated={(c) => {
          setClients(prev => [...(prev || []), c]);
          setSelectedClient(c.id);
          if (!cantiereEdited) setCantiere(c.cantiere || c.address || '');
        }}
      />
    </div>
  );
}
